import { CreateFlowFilterPanel } from "#batteries-included-components/Panels/FilterPanels/CreateFlowFilterPanel";
import {
  getDownstreamAssetsFromFlow,
  getUpstreamAssetsFromFlow,
  selectedAssetsToFlowFields,
} from "#batteries-included-components/Panels/FormPanels/FlowFormPanel/FlowConnectionsStep.helpers";
import { useSearchParams } from "#routers/hooks";
import { useGetOneFlow } from "#src/components/hooks/adapters/useFlows";
import { DEFAULT_DATE_RANGES } from "#src/hooks/useDateRange";
import useLocalization from "#src/hooks/useLocalization";
import { useMultiStepFormContext } from "#src/hooks/useMultiStepForm";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { Panel } from "@validereinc/common-components";
import { AssetGroupAssetType, AssetType } from "@validereinc/domain";
import { getYearMonthFromDateRange } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useEffect, useState } from "react";
import { FlowEquipmentSelectionTablePanel } from "./FlowEquipmentSelectionTablePanel";
import { FlowFacilitySelectionTablePanel } from "./FlowFacilitySelectionTablePanel";
import styles from "./FlowFormPanel.module.scss";
import { FlowSelectionTablePanel } from "./FlowSelectionTablePanel";
import { SelectedAssetsPanel } from "./SelectedAssetsPanel";

const cx = classNames.bind(styles);

export const FlowConnectionStep = ({
  stepNumberOverride,
  flowId,
}: {
  stepNumberOverride?: number;
  flowId: string;
}) => {
  const STEP_NUMBER = stepNumberOverride ?? 3;
  const { currentStep, updateStep } = useMultiStepFormContext();
  const [filterConfigStorageKey] = useStorageKey(
    ["filter-configuration"],
    "flow-connections-step"
  );
  const [searchParams] = useSearchParams();

  const { localize, isLoading: isLocalizationLoading } = useLocalization();

  const { data: flowData } = useGetOneFlow(
    {
      id: flowId,
      meta: {
        period: getYearMonthFromDateRange(
          DEFAULT_DATE_RANGES.currentWholeMonth
        ),
      },
    },
    {
      enabled: !!flowId && currentStep === STEP_NUMBER,
    }
  );
  const flow = flowData?.data;

  const upstreamAssets = flow ? getUpstreamAssetsFromFlow(flow) : [];
  const downstreamAssets = flow ? getDownstreamAssetsFromFlow(flow) : [];

  const [selectedOrigins, setSelectedOrigins] =
    // @ts-expect-error TODO: fix filter typing
    useState<AssetGroupAssetType[]>(upstreamAssets);
  const [selectedDestinations, setSelectedDestinations] =
    // @ts-expect-error TODO: fix filter typing
    useState<AssetGroupAssetType[]>(downstreamAssets);

  useEffect(() => {
    // @ts-expect-error TODO: fix filter typing
    setSelectedOrigins(upstreamAssets);
    // @ts-expect-error TODO: fix filter typing
    setSelectedDestinations(downstreamAssets);
  }, [flow]);

  const addAssetToFlow = (asset: AssetGroupAssetType, isUpstream: boolean) => {
    if (isUpstream) {
      const existingOrigins =
        asset.asset_type === AssetType.FLOW
          ? selectedOrigins.filter(
              ({ asset_type }) => asset_type === asset.asset_type
            )
          : [];

      setSelectedOrigins([...existingOrigins, asset]);
    } else {
      const existingDestinations = selectedDestinations.filter(
        ({ asset_type }) => asset_type === asset.asset_type
      );
      setSelectedDestinations([...existingDestinations, asset]);
    }
  };

  const removeAssetFromFlow = (
    asset: AssetGroupAssetType,
    isUpstream: boolean
  ) => {
    if (isUpstream) {
      setSelectedOrigins(selectedOrigins.filter(({ id }) => id !== asset.id));
    } else {
      setSelectedDestinations(
        selectedDestinations.filter(({ id }) => id !== asset.id)
      );
    }
  };

  const selectedAssets = Object.fromEntries(
    [...selectedOrigins, ...selectedDestinations].map((asset) => [
      asset.id,
      asset,
    ])
  );

  useEffect(() => {
    const values = {
      ...selectedAssetsToFlowFields(selectedOrigins, true),
      ...selectedAssetsToFlowFields(selectedDestinations, false),
    };
    updateStep(STEP_NUMBER, {
      getValues: () => values as any,
      getFormState: () => ({
        isValid: true,
        isSubmitting: false,
      }),
    });
  }, [selectedOrigins, selectedDestinations]);

  const selectionTablePanelProps = {
    filterConfigStorageKey,
    selectedAssets,
    addAssetToFlow,
  };

  return (
    <>
      {currentStep === STEP_NUMBER ? (
        <Panel
          className={cx("panel")}
          title="Connections"
          loaded={!isLocalizationLoading}
        >
          <p>
            Select the origin and destination asset(s) of your{" "}
            {localize("Flow")}
          </p>

          <div>
            <CreateFlowFilterPanel
              filterConfigStorageKey={filterConfigStorageKey}
            />

            <div className={cx("connectionsPanelsContainer")}>
              {searchParams.entity_type === AssetType.FACILITY ? (
                <FlowFacilitySelectionTablePanel
                  {...selectionTablePanelProps}
                />
              ) : null}

              {searchParams.entity_type === AssetType.EQUIPMENT ? (
                <FlowEquipmentSelectionTablePanel
                  {...selectionTablePanelProps}
                />
              ) : null}

              {searchParams.entity_type === AssetType.FLOW ? (
                <FlowSelectionTablePanel {...selectionTablePanelProps} />
              ) : null}

              <div className={cx("selectedAssetsPanelContainer")}>
                <SelectedAssetsPanel
                  selectedOrigins={selectedOrigins}
                  selectedDestinations={selectedDestinations}
                  removeAssetFromFlow={removeAssetFromFlow}
                />
              </div>
            </div>
          </div>
        </Panel>
      ) : null}
    </>
  );
};
